import React, { useState } from 'react';
import { CreditCard, Check, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { useTranslation } from '../lib/LanguageContext';

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type BillingCycle = 'monthly' | 'yearly';

interface PricingOption {
  id: BillingCycle;
  label: string;
  price: number;
  period: string;
  savings?: string;
  popular?: boolean;
}

export function SubscriptionModal({ isOpen, onClose, onSuccess }: SubscriptionModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedBilling, setSelectedBilling] = useState<BillingCycle>('yearly');
  const { user } = useAuth();
  const { t } = useTranslation();

  const pricingOptions: PricingOption[] = [
    {
      id: 'monthly',
      label: t('monthly'),
      price: 5,
      period: t('perMonth'),
    },
    {
      id: 'yearly',
      label: t('yearly'),
      price: 48,
      period: t('perYear'),
      savings: t('savePerYear'),
      popular: true,
    }
  ];
  
  if (!isOpen) return null;
  
  const selectedOption = pricingOptions.find(option => option.id === selectedBilling)!;
  
  const handleCheckout = async () => {
    if (!user) {
      setError(t('mustBeLoggedIn'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Check if user is a sandbox test user
      const { data: isTestUser } = await supabase.rpc('get_is_test_user');
      const edgeFunction = isTestUser ? 'polar-test' : 'polar';

      // Call Polar edge function to create Checkout
      const { data, error } = await supabase.functions.invoke(edgeFunction, {
        body: {
          action: 'create-checkout',
          userId: user.id,
          billingCycle: selectedBilling,
          email: user.email,
          redirectBase: window.location.origin
        }
      });

      if (error) throw error;

      if (data?.url) {
        // Redirect to Polar Checkout
        window.location.href = data.url;
      } else {
        throw new Error(t('failedToCreatePayment'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errorOccurred'));
      setLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex justify-between items-center border-b px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('upgradeToPremiumTitle')}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-6">
          {/* Billing Cycle Selection */}
          <div className="mb-6">
            <h4 className="text-base font-medium text-gray-900 mb-4">{t('chooseBillingCycle')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {pricingOptions.map((option) => (
                <div key={option.id} className="relative">
                  <label className="cursor-pointer">
                    <input
                      type="radio"
                      name="billing"
                      value={option.id}
                      checked={selectedBilling === option.id}
                      onChange={(e) => setSelectedBilling(e.target.value as BillingCycle)}
                      className="sr-only"
                    />
                    <div className={`relative border-2 rounded-lg p-4 transition-all duration-200 ${
                      selectedBilling === option.id 
                        ? 'border-indigo-500 bg-indigo-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      {/* Popular badge */}
                      {option.popular && (
                        <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                          <span className="bg-indigo-600 text-white text-xs font-medium px-2 py-1 rounded-full">
                            {t('mostPopular')}
                          </span>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center">
                            <span className="text-lg font-semibold text-gray-900">
                              {option.label}
                            </span>
                            {selectedBilling === option.id && (
                              <Check className="h-5 w-5 text-indigo-600 ml-2" />
                            )}
                          </div>
                          <div className="mt-1">
                            <span className="text-2xl font-bold text-gray-900">
                              {option.id === 'monthly' ? t('monthlyPriceValue') : t('yearlyPriceValue')}
                            </span>
                            <span className="text-gray-500 ml-1">
                              {option.period}
                            </span>
                          </div>
                          {option.savings && (
                            <div className="mt-1">
                              <span className="text-sm text-green-600 font-medium">
                                {option.savings}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Selected Plan Summary */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h5 className="font-medium text-gray-900 mb-2">{t('selectedPlan')}</h5>
            <div className="flex justify-between items-center">
              <span className="text-gray-700">
                {t('premium')} {selectedOption.label}
              </span>
              <span className="font-semibold text-gray-900">
                {selectedOption.id === 'monthly' ? t('monthlyPriceValue') : t('yearlyPriceValue')} {selectedOption.period}
              </span>
            </div>
            {selectedOption.savings && (
              <div className="mt-1 text-sm text-green-600">
                {selectedOption.savings}
              </div>
            )}
          </div>

          {/* Premium Features */}
          <div className="mb-6">
            <h4 className="text-base font-medium text-gray-900 mb-2">{t('premiumFeatures')}</h4>
            <ul className="space-y-2">
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span>{t('increasedTaskLimit')}</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span>{t('unlimitedTemplatesFeature')}</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span>{t('aiSuggestionsFeature')}</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span>{t('shareStickyNotesFeature')}</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span>{t('prioritySupportFeature')}</span>
              </li>
            </ul>
          </div>
          
          {error && (
            <div className="text-red-500 text-sm mb-4 p-3 bg-red-50 rounded-md">{error}</div>
          )}
          
          <div className="flex justify-end space-x-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              {t('cancel')}
            </button>
            <button
              onClick={handleCheckout}
              disabled={loading}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('processingButton')}
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  {t('proceedToPayment')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}